import React, { useEffect, useState } from "react";
import { Plus, RotateCcw, Trash2, ChevronDown, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { SaveFavoriteButton } from "./SaveFavoriteButton";

const KIND = { "Bail d'habitation meublé": "bail_meuble", "Bail meublé étudiant": "bail_meuble", "Bail colocation": "bail_colocation", "Bail mobilité": "bail_mobilite", "Bail commercial": "bail_commercial", "Bail professionnel": "bail_professionnel", "Bail parking / garage": "bail_parking" };
const AUTO = { "Bail commercial": "commercial", "Bail professionnel": "professionnel", "Bail de droit commun": "droit_commun" };

function ClauseRow({ c, checked, value, onToggle, onEdit, onRemove, extra }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border px-3 py-2 transition-colors ${checked ? "border-brand-limeDark bg-brand-lime/10" : "border-brand-soft"}`} data-testid={`clause-row-${c.key}`}>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={checked} onChange={onToggle} className="accent-[#9DB43A] w-4 h-4" data-testid={`clause-check-${c.key}`} />
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 text-left text-sm font-medium text-brand-greenDeep" data-testid={`clause-toggle-${c.key}`}>{value?.title || c.title}</button>
        {extra}
        {onRemove && <button type="button" onClick={onRemove} className="text-brand-green/40 hover:text-red-500" data-testid={`clause-remove-${c.key}`}><Trash2 className="w-4 h-4" /></button>}
        <ChevronDown className={`w-4 h-4 text-brand-green/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          <input value={value?.title ?? c.title} disabled={!checked} onChange={(e) => onEdit({ title: e.target.value })} className="input text-sm" data-testid={`clause-title-${c.key}`} />
          <textarea value={value?.text ?? c.text} disabled={!checked} onChange={(e) => onEdit({ text: e.target.value })} rows={5} className="input text-sm leading-relaxed" data-testid={`clause-text-${c.key}`} />
        </div>
      )}
    </div>
  );
}

export const LeaseClauses = ({ form, setForm }) => {
  const [tpls, setTpls] = useState(null);
  const [favs, setFavs] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/lease-templates"), api.get("/lease-clauses/favorites")])
      .then(([t, f]) => { setFavs(f.data); setTpls(t.data); }).catch(() => { setFavs([]); setTpls({}); });
  }, []);
  const favsFor = (m) => (favs || []).filter((f) => !f.model || f.model === m);
  const favClause = (f) => ({ key: `fav_${f.id}`, title: f.title, text: f.text });
  const build = (m) => {
    const fs = favsFor(m); const replaced = new Set(fs.filter((f) => f.source_key).map((f) => f.source_key));
    return [...(tpls?.[m]?.clauses || []).filter((c) => c.default && !replaced.has(c.key)).map(({ key, title, text }) => ({ key, title, text })), ...fs.map(favClause)];
  };
  const model = form.clause_model || "";
  const clauses = form.clauses || [];
  const tpl = tpls?.[model];

  const isCustomized = () => {
    const defs = build(form.clause_model || "");
    const cl = form.clauses || [];
    return cl.length !== defs.length || cl.some((c) => { const d = defs.find((x) => x.key === c.key); return !d || d.text !== c.text || d.title !== c.title; });
  };
  const applyModel = (m, ask = true) => {
    if (ask && (form.clauses || []).length && isCustomized() && !window.confirm("Remplacer les clauses actuelles (modifications et clauses personnalisées perdues) ?")) return;
    const t = tpls?.[m];
    setForm((f) => ({ ...f, clause_model: m, clauses: build(m), index_type: t ? t.index : f.index_type }));
  };
  useEffect(() => {
    if (!tpls) return;
    if (!form.id && !AUTO[form.type] && !form.clause_model && !(form.clauses || []).length && favsFor("").length) { applyModel("", false); return; }
    if (!AUTO[form.type] || form.clause_model === AUTO[form.type]) return;
    if (!form.clause_model) { if (!form.id) applyModel(AUTO[form.type], (form.clauses || []).length > 0); }
    else if (form.clause_model) applyModel(AUTO[form.type]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpls, form.type]);

  const byKey = Object.fromEntries(clauses.map((c) => [c.key, c]));
  const toggle = (c) => setForm((f) => ({ ...f, clauses: byKey[c.key] ? f.clauses.filter((x) => x.key !== c.key) : [...(f.clauses || []), { key: c.key, title: c.title, text: c.text }] }));
  const edit = (key, patch) => setForm((f) => ({ ...f, clauses: f.clauses.map((x) => (x.key === key ? { ...x, ...patch } : x)) }));
  const customs = clauses.filter((c) => c.key.startsWith("custom_"));
  const favRows = favsFor(model);
  const favKeys = new Set(favRows.map((f) => `fav_${f.id}`));
  const orphanFavs = clauses.filter((c) => c.key.startsWith("fav_") && !favKeys.has(c.key));
  const onSaved = (f, srcKey) => {
    setFavs((l) => [...(l || []).filter((x) => x.id !== f.id), f]);
    setForm((x) => ({ ...x, clauses: x.clauses.map((c) => (c.key === srcKey ? favClause(f) : c)).filter((c, i, arr) => arr.findIndex((y) => y.key === c.key) === i) }));
  };
  const delFav = async (f) => {
    if (!window.confirm(`Retirer « ${f.title} » de vos clauses favorites ? (elle reste dans ce bail si cochée)`)) return;
    await api.delete(`/lease-clauses/favorites/${f.id}`);
    setFavs((l) => l.filter((x) => x.id !== f.id)); toast.success("Clause retirée des favoris");
  };
  const star = (c) => (byKey[c.key] ? <SaveFavoriteButton clause={byKey[c.key]} model={model} modelLabel={tpl?.label} onSaved={onSaved} /> : null);
  const addCustom = () => setForm((f) => ({ ...f, clauses: [...(f.clauses || []), { key: `custom_${Date.now()}`, title: "Clause particulière", text: "" }] }));

  return (
    <div className="space-y-3" data-testid="lease-clauses">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={model} onChange={(e) => applyModel(e.target.value)} className="input flex-1 min-w-[200px]" data-testid="clause-model-select">
          <option value="">Aucun modèle de clauses</option>
          {Object.values(tpls || {}).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        {tpl && <button type="button" onClick={() => applyModel(model)} className="inline-flex items-center gap-1 text-xs text-brand-green/60 hover:text-brand-greenDeep" data-testid="clause-reset-btn"><RotateCcw className="w-3.5 h-3.5" /> Réinitialiser</button>}
      </div>
      {tpl && (
        <>
          <p className="text-[11px] text-brand-green/50">Cochez les clauses à inclure, cliquez pour modifier le texte. Variables remplacées dans le PDF : {"{loyer}, {loyer_annuel}, {depot}, {debut}, {activite}, {indice}"}.</p>
          <label className="block text-xs font-medium text-brand-green/70">Activité / destination autorisée
            <input value={form.activite || ""} onChange={(e) => setForm((f) => ({ ...f, activite: e.target.value }))} placeholder="Ex. : restauration traditionnelle, cabinet d'avocat, stockage…" className="input mt-1" data-testid="clause-activite" />
          </label>
          <div className="space-y-2">
            {tpl.clauses.map((c) => <ClauseRow key={c.key} c={c} checked={!!byKey[c.key]} value={byKey[c.key]} onToggle={() => toggle(c)} onEdit={(p) => edit(c.key, p)} extra={star(c)} />)}
          </div>
        </>
      )}
      {favRows.length > 0 && (
        <div className="space-y-2" data-testid="clause-favorites">
          <p className="text-xs font-semibold text-brand-greenDeep flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" /> Mes clauses favorites</p>
          {favRows.map((f) => { const c = favClause(f); return (
            <ClauseRow key={c.key} c={c} checked={!!byKey[c.key]} value={byKey[c.key]} onToggle={() => toggle(c)} onEdit={(p) => edit(c.key, p)}
              extra={<span className="text-[10px] px-1.5 rounded-full bg-brand-softer text-brand-green/60">{f.model ? tpls?.[f.model]?.label : "Tous les baux"}</span>}
              onRemove={() => delFav(f)} />); })}
        </div>
      )}
      {[...orphanFavs, ...customs].map((c) => <ClauseRow key={c.key} c={c} checked value={c} onToggle={() => toggle(c)} onEdit={(p) => edit(c.key, p)} onRemove={() => toggle(c)} extra={c.key.startsWith("custom_") && (c.text || "").trim() ? <SaveFavoriteButton clause={c} model={model} modelLabel={tpl?.label} onSaved={onSaved} /> : null} />)}
      <button type="button" onClick={addCustom} className="inline-flex items-center gap-2 text-sm font-medium text-brand-greenDeep hover:text-brand-limeDark" data-testid="clause-add-custom"><Plus className="w-4 h-4" /> Ajouter une clause personnalisée</button>
      <p className="text-xs text-brand-green/60" data-testid="clause-count">{clauses.length} clause(s) incluse(s) dans le contrat</p>
      <a href={`/app/modeles-documents?kind=${KIND[form.type] || "bail_vide"}`} target="_blank" rel="noreferrer" className="inline-block text-xs text-brand-limeDark hover:underline" data-testid="lease-template-link">Personnaliser la mise en page et les textes des baux →</a>
    </div>
  );
};
